import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { adminApiError, adminApiMessage } from '../adminApi';
import { StatusBadge } from '../components/StatusBadge';
import { EvalBadge, EvalError, RunLink } from '../evals/EvalBits';
import { modelShortName } from '../evals/format';
import { groupByPromptLabel } from '../evals/promptVersion';
import {
  CONSENTS_HREF,
  QUEUE_WINDOWS,
  candidateHref,
  candidateSignals,
  candidateTitle,
  exportFileName,
  inCorpusOutcome,
  isExpertAgent,
  kindLabel,
  queueFilterFromSearch,
  queueFilterSearch,
  gateHref,
  refusalHref,
  sourceLabel,
  stateLabel,
  stateTone,
  targetAgentLabel,
  transcriptHref,
  type EvalQueueFilter,
} from '../evals/inbox';
import { inboxApi } from '../evals/inboxApi';
import {
  EVAL_CANDIDATE_SOURCES,
  EVAL_CASE_TARGET_AGENTS,
  EVAL_TRIAGE_STATES,
  type EvalCandidateCounts,
  type EvalCandidateList,
  type EvalCandidateSummary,
  type EvalCaseExportOutcome,
  type EvalCaseLeftOut,
} from '../evals/inboxTypes';
import { GuideLink } from '../evals/GuideLink';
import { formatDate } from '../format';

// The eval case inbox's queue (TOM-140, ADR 0233): every failing production
// turn, one candidate per assistant turn, with counts by source, prompt
// version and triage state. The rows are content-free as the read model
// returns them (ADR 0119): enumerated signal details and ids, never chat text.
// Reading a turn goes through its transcript, or the refusals list for a
// refusal, which ask for a reason and are audited fail-closed. The filters
// live in the page URL. Accepted cases leave through "Export accepted".

function SignalChips({ c }: { c: EvalCandidateSummary }) {
  return (
    <span className="eval-inbox-signals">
      {candidateSignals(c).map((s) => (
        <span key={s.source} className="eval-inbox-signal">
          <EvalBadge tone="neutral">
            {s.label}
            {s.count > 1 ? ` ×${s.count}` : ''}
          </EvalBadge>
          {s.details.length > 0 ? (
            <span className="admin-muted eval-sub">{s.details.join(', ')}</span>
          ) : null}
        </span>
      ))}
    </span>
  );
}

function ReadLink({ c }: { c: EvalCandidateSummary }) {
  const transcript = transcriptHref(c);
  if (transcript !== null) {
    return (
      <Link to={transcript} title="Asks for a reason; the read is audited">
        Transcript →
      </Link>
    );
  }
  const refusal = refusalHref(c);
  if (refusal !== null) {
    return (
      <Link to={refusal} title="Asks for a reason; the read is audited">
        Refusal →
      </Link>
    );
  }
  const gate = gateHref(c);
  if (gate !== null) {
    return (
      <Link to={gate} title="Asks for a reason; the read is audited">
        Gate →
      </Link>
    );
  }
  return (
    <span className="admin-muted" title="Its source was erased">
      Erased
    </span>
  );
}

function CorpusCell({ c }: { c: EvalCandidateSummary }) {
  if (c.inCorpus === null) {
    return c.exportedAt !== null ? (
      <span className="admin-muted eval-sub">Exported {formatDate(c.exportedAt)}</span>
    ) : (
      <span className="admin-muted">—</span>
    );
  }
  const outcome = inCorpusOutcome(c.inCorpus);
  return (
    <span className="eval-stack">
      <span>
        <EvalBadge tone={outcome.tone}>{outcome.text}</EvalBadge>
      </span>
      <span className="admin-muted eval-sub eval-nowrap">
        <RunLink id={c.inCorpus.latest.runId} /> ·{' '}
        <span title={c.inCorpus.latest.model}>{modelShortName(c.inCorpus.latest.model)}</span>
      </span>
      <span className="admin-muted eval-sub">
        First run <RunLink id={c.inCorpus.firstRunId} />, {c.inCorpus.results}{' '}
        {c.inCorpus.results === 1 ? 'result' : 'results'}
      </span>
    </span>
  );
}

function CandidateRow({ c }: { c: EvalCandidateSummary }) {
  return (
    <tr data-testid={`eval-candidate-${c.key}`}>
      <td data-label="Candidate">
        <span className="eval-stack">
          <Link to={candidateHref(c)} className="eval-inbox-open">
            {candidateTitle(c)}
          </Link>
          <span className="admin-muted eval-sub eval-nowrap">
            {c.occurredAt !== null ? formatDate(c.occurredAt) : 'Source erased'}
          </span>
          {c.tryIt ? (
            <span>
              <StatusBadge status="ai" label="Try it" />
            </span>
          ) : null}
        </span>
      </td>
      <td data-label="State">
        <EvalBadge tone={stateTone(c.state)} testId="eval-candidate-state">
          {stateLabel(c.state)}
        </EvalBadge>
      </td>
      <td data-label="Signals">
        <SignalChips c={c} />
      </td>
      <td data-label="Prompt">{c.promptVersion !== null ? <code>{c.promptVersion}</code> : '—'}</td>
      <td data-label="Case">
        {c.caseId !== null ? (
          <span className="eval-stack">
            <code className="eval-inbox-case-id">{c.caseId}</code>
            <span className="admin-muted eval-sub">
              {[
                c.kind !== null ? kindLabel(c.kind) : null,
                c.category,
                c.dataSource === 'moment'
                  ? `Moment${c.split !== null ? ` · ${c.split}` : ''}`
                  : c.dataSource === 'stub_boat'
                    ? 'Stub boat'
                    : null,
              ]
                .filter((x) => x !== null)
                .join(' · ')}
            </span>
            {isExpertAgent(c.targetAgent) ? (
              <span className="eval-sub">
                <EvalBadge tone="neutral" testId="eval-candidate-agent">
                  For {targetAgentLabel(c.targetAgent)}
                </EvalBadge>
              </span>
            ) : null}
          </span>
        ) : (
          <span className="admin-muted">—</span>
        )}
      </td>
      <td data-label="Corpus">
        <CorpusCell c={c} />
      </td>
      <td data-label="Read" className="eval-nowrap">
        <ReadLink c={c} />
      </td>
    </tr>
  );
}

type VersionCount = EvalCandidateCounts['byPromptVersion'][number];

/** A label's versions added up, for its line above them (cloud ADR 0332). */
function sumVersionCounts(label: string | null, rows: readonly VersionCount[]): VersionCount {
  const bySource = Object.fromEntries(
    EVAL_CANDIDATE_SOURCES.map((s) => [s, rows.reduce((n, v) => n + v.bySource[s], 0)])
  ) as VersionCount['bySource'];
  return {
    promptVersion: label,
    candidates: rows.reduce((n, v) => n + v.candidates, 0),
    bySource,
  };
}

/**
 * One line of the counts by prompt version. A label with several versions
 * leads with its total, and each exact version follows, indented.
 */
function VersionCountRow({ v, name, sub }: { v: VersionCount; name?: ReactNode; sub?: boolean }) {
  return (
    <tr className={sub === true ? 'admin-version-sub' : undefined}>
      <td>
        {name ??
          (v.promptVersion !== null ? (
            <code>{v.promptVersion}</code>
          ) : (
            <span className="admin-muted">None</span>
          ))}
      </td>
      <td>{v.candidates}</td>
      {EVAL_CANDIDATE_SOURCES.map((s) => (
        <td key={s} className={v.bySource[s] === 0 ? 'admin-muted' : undefined}>
          {v.bySource[s]}
        </td>
      ))}
    </tr>
  );
}

function Counts({
  counts,
  filter,
  onFilter,
}: {
  counts: EvalCandidateCounts;
  filter: EvalQueueFilter;
  onFilter: (f: EvalQueueFilter) => void;
}) {
  return (
    <div className="eval-inbox-counts" data-testid="eval-inbox-counts">
      <div className="eval-inbox-count-group">
        <h2>By state</h2>
        <div className="eval-inbox-chips">
          {EVAL_TRIAGE_STATES.map((s) => (
            <button
              key={s}
              type="button"
              className={`eval-inbox-chip${filter.state === s ? ' eval-inbox-chip--on' : ''}`}
              aria-pressed={filter.state === s}
              onClick={() => onFilter({ ...filter, state: filter.state === s ? undefined : s })}
            >
              {stateLabel(s)} <strong>{counts.byState[s]}</strong>
            </button>
          ))}
        </div>
      </div>
      <div className="eval-inbox-count-group">
        <h2>By source</h2>
        <div className="eval-inbox-chips">
          {EVAL_CANDIDATE_SOURCES.map((s) => (
            <button
              key={s}
              type="button"
              className={`eval-inbox-chip${filter.source === s ? ' eval-inbox-chip--on' : ''}`}
              aria-pressed={filter.source === s}
              title={`${counts.bySource[s]} candidates, ${counts.signalsBySource[s]} signals`}
              onClick={() => onFilter({ ...filter, source: filter.source === s ? undefined : s })}
            >
              {sourceLabel(s)} <strong>{counts.bySource[s]}</strong>
              {counts.signalsBySource[s] !== counts.bySource[s] ? (
                <span className="admin-muted"> · {counts.signalsBySource[s]} signals</span>
              ) : null}
            </button>
          ))}
        </div>
      </div>
      {counts.byPromptVersion.length > 0 ? (
        <div className="eval-inbox-count-group">
          <h2>By prompt version</h2>
          <div className="scroll-x">
            <table
              className="admin-table eval-table eval-table--compact"
              data-testid="eval-inbox-versions"
            >
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Candidates</th>
                  {EVAL_CANDIDATE_SOURCES.map((s) => (
                    <th key={s}>{sourceLabel(s)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groupByPromptLabel(counts.byPromptVersion, (v) => v.promptVersion).map((g) =>
                  g.rows.length === 1 ? (
                    <VersionCountRow key={g.label ?? '(none)'} v={g.rows[0]!} />
                  ) : (
                    <Fragment key={g.label ?? '(none)'}>
                      <VersionCountRow
                        v={sumVersionCounts(g.label, g.rows)}
                        name={
                          <>
                            <strong>{g.label}</strong>{' '}
                            <span className="admin-muted">· {g.rows.length} versions</span>
                          </>
                        }
                      />
                      {g.rows.map((v) => (
                        <VersionCountRow key={v.promptVersion ?? '(none)'} v={v} sub />
                      ))}
                    </Fragment>
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Filters({
  filter,
  onFilter,
}: {
  filter: EvalQueueFilter;
  onFilter: (f: EvalQueueFilter) => void;
}) {
  return (
    <form
      className="eval-filters"
      aria-label="Filter candidates"
      onSubmit={(e) => e.preventDefault()}
    >
      <label>
        Window
        <select
          value={filter.days}
          onChange={(e) => onFilter({ ...filter, days: Number(e.target.value) })}
          data-testid="eval-inbox-window"
        >
          {QUEUE_WINDOWS.map((d) => (
            <option key={d} value={d}>
              Last {d} days
            </option>
          ))}
        </select>
      </label>
      <label>
        State
        <select
          value={filter.state ?? ''}
          onChange={(e) =>
            onFilter({
              ...filter,
              state: (e.target.value || undefined) as EvalQueueFilter['state'],
            })
          }
          data-testid="eval-inbox-state"
        >
          <option value="">Any</option>
          {EVAL_TRIAGE_STATES.map((s) => (
            <option key={s} value={s}>
              {stateLabel(s)}
            </option>
          ))}
        </select>
      </label>
      <label>
        Source
        <select
          value={filter.source ?? ''}
          onChange={(e) =>
            onFilter({
              ...filter,
              source: (e.target.value || undefined) as EvalQueueFilter['source'],
            })
          }
          data-testid="eval-inbox-source"
        >
          <option value="">Any</option>
          {EVAL_CANDIDATE_SOURCES.map((s) => (
            <option key={s} value={s}>
              {sourceLabel(s)}
            </option>
          ))}
        </select>
      </label>
      <label>
        For
        <select
          value={filter.agent ?? ''}
          onChange={(e) =>
            onFilter({
              ...filter,
              agent: (e.target.value || undefined) as EvalQueueFilter['agent'],
            })
          }
          data-testid="eval-inbox-agent"
        >
          <option value="">Any agent</option>
          {EVAL_CASE_TARGET_AGENTS.map((a) => (
            <option key={a} value={a}>
              {targetAgentLabel(a)}
            </option>
          ))}
        </select>
      </label>
    </form>
  );
}

function LeftOutList({ leftOut }: { leftOut: EvalCaseLeftOut[] }) {
  if (leftOut.length === 0) return null;
  return (
    <>
      <p>Left out:</p>
      <ul className="eval-list" data-testid="eval-export-left-out">
        {leftOut.map((l) => (
          <li key={l.caseId}>
            <code>{l.caseId}</code>: {l.reason}
          </li>
        ))}
      </ul>
    </>
  );
}

/** Save the export as a file the import reads, named by deployment and time. */
function download(outcome: EvalCaseExportOutcome): void {
  const blob = new Blob([`${JSON.stringify(outcome.file, null, 2)}\n`], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = exportFileName(outcome.file);
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function ExportPanel({ accepted, onExported }: { accepted: number; onExported: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<
    | { kind: 'ok'; outcome: EvalCaseExportOutcome }
    | { kind: 'nothing'; leftOut: EvalCaseLeftOut[] }
    | { kind: 'error'; message: string }
    | null
  >(null);

  async function run() {
    setBusy(true);
    setResult(null);
    try {
      const outcome = await inboxApi.exportAccepted();
      download(outcome);
      setResult({ kind: 'ok', outcome });
      onExported();
    } catch (err) {
      const body = adminApiError(err) as { error?: string; leftOut?: EvalCaseLeftOut[] };
      if (body.error === 'nothing_to_export') {
        setResult({ kind: 'nothing', leftOut: body.leftOut ?? [] });
      } else {
        setResult({ kind: 'error', message: adminApiMessage(err, 'The export failed.') });
      }
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  return (
    <div className="eval-inbox-export" data-testid="eval-export">
      {confirming ? (
        <div className="eval-callout eval-inbox-confirm" role="group" aria-label="Export accepted">
          <p>
            The export downloads every accepted case not yet in the corpus as the file{' '}
            <code>pnpm neo:eval:import-cases</code> reads. A case that goes out stays accepted from
            then on: it may be in git, so a fix to it is a change in git.
          </p>
          <div className="eval-actions">
            <button
              type="button"
              className="admin-primary-button admin-button-inline"
              disabled={busy}
              onClick={() => void run()}
              data-testid="eval-export-confirm"
            >
              {busy ? 'Exporting…' : 'Export and download'}
            </button>
            <button
              type="button"
              className="admin-button-secondary"
              disabled={busy}
              onClick={() => setConfirming(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="admin-button-secondary"
          onClick={() => setConfirming(true)}
          data-testid="eval-export-start"
        >
          Export accepted{accepted > 0 ? ` (${accepted})` : ''}…
        </button>
      )}
      {result !== null ? (
        <div
          className={result.kind === 'error' ? 'admin-auth-banner error' : 'eval-upload-result'}
          role="status"
          data-testid="eval-export-result"
        >
          {result.kind === 'ok' ? (
            <>
              <p>
                Downloaded <code>{exportFileName(result.outcome.file)}</code>:{' '}
                {result.outcome.file.cases.length}{' '}
                {result.outcome.file.cases.length === 1 ? 'case' : 'cases'}
                {result.outcome.firstExported.length > 0
                  ? `, ${result.outcome.firstExported.length} going out for the first time`
                  : ', every one of them out before'}
                .
              </p>
              <LeftOutList leftOut={result.outcome.leftOut} />
            </>
          ) : result.kind === 'nothing' ? (
            <>
              <p>No accepted case can go out.</p>
              <LeftOutList leftOut={result.leftOut} />
            </>
          ) : (
            result.message
          )}
        </div>
      ) : null}
    </div>
  );
}

export function AdminEvalCases() {
  const [search, setSearch] = useSearchParams();
  const filter = queueFilterFromSearch(search);
  const filterKey = queueFilterSearch(filter);
  const [data, setData] = useState<EvalCandidateList | null>(null);
  const [candidates, setCandidates] = useState<EvalCandidateSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let ignore = false;
    setError(null);
    inboxApi
      .queue(queueFilterFromSearch(new URLSearchParams(filterKey)))
      .then((res) => {
        if (ignore) return;
        setData(res);
        setCandidates(res.candidates);
      })
      .catch((err) => {
        if (!ignore) setError(adminApiMessage(err, 'Failed to load the eval case queue.'));
      });
    return () => {
      ignore = true;
    };
  }, [filterKey, reload]);

  function apply(next: EvalQueueFilter) {
    setSearch(new URLSearchParams(queueFilterSearch(next)));
  }

  async function loadMore() {
    if (data?.nextCursor == null) return;
    setLoadingMore(true);
    try {
      const res = await inboxApi.queue(filter, data.nextCursor);
      setData(res);
      setCandidates((prev) => [...prev, ...res.candidates]);
    } catch (err) {
      setError(adminApiMessage(err, 'Failed to load more candidates.'));
    } finally {
      setLoadingMore(false);
    }
  }

  const narrowed =
    filter.state !== undefined || filter.source !== undefined || filter.agent !== undefined;

  return (
    <section className="admin-section eval-page eval-inbox">
      <header className="admin-section-header">
        <h1>Eval cases</h1>
        <span className="admin-count">
          {data !== null
            ? `${data.counts.candidates} ${data.counts.candidates === 1 ? 'candidate' : 'candidates'} in ${data.windowDays} days`
            : null}
        </span>
        <GuideLink section="cases-inbox" />
      </header>
      <p className="admin-hint">
        Failing production turns, one candidate per assistant turn: thumbs-down, output-gate blocks
        (model refusals among them), review tags, input-gate refusals, turns where the input
        gate&apos;s Jev and regex disagreed, and promoted turns. Rows hold no chat text. To read a
        turn, open its transcript, refusal or disagreement, which asks for a reason and is audited.
      </p>

      <div className="eval-actions">
        <ExportPanel
          accepted={data?.counts.byState.accepted ?? 0}
          onExported={() => setReload((n) => n + 1)}
        />
        <Link to={CONSENTS_HREF} data-testid="eval-consents-link">
          Recorded-moment consents →
        </Link>
      </div>

      <EvalError message={error} />
      {data === null && error === null ? <p className="admin-loading-row">Loading…</p> : null}

      {data !== null ? (
        <>
          <Counts counts={data.counts} filter={filter} onFilter={apply} />
          <Filters filter={filter} onFilter={apply} />
          {candidates.length === 0 ? (
            <div className="admin-empty-state" data-testid="eval-inbox-empty">
              <p className="admin-empty-state-title">
                {narrowed ? 'No candidates match.' : 'No candidates in this window.'}
              </p>
              <p className="admin-hint">
                A turn becomes a candidate when an owner rates it down, an output gate blocks it, a
                reviewer tags or promotes it in the Neo chats, the input gate refuses a question, or
                the input gate&apos;s Jev and regex disagree about a message.
              </p>
            </div>
          ) : (
            <>
              <div className="scroll-x">
                <table
                  className="admin-table eval-table eval-inbox-table"
                  data-testid="eval-inbox-table"
                >
                  <thead>
                    <tr>
                      <th>Candidate</th>
                      <th>State</th>
                      <th>Signals</th>
                      <th>Prompt</th>
                      <th>Case</th>
                      <th>Corpus</th>
                      <th>Read</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map((c) => (
                      <CandidateRow key={c.key} c={c} />
                    ))}
                  </tbody>
                </table>
              </div>
              {data.nextCursor !== null ? (
                <p className="eval-pager">
                  <button
                    type="button"
                    className="admin-link-button"
                    disabled={loadingMore}
                    onClick={() => void loadMore()}
                  >
                    {loadingMore ? 'Loading…' : 'Older candidates →'}
                  </button>
                </p>
              ) : null}
            </>
          )}
        </>
      ) : null}
    </section>
  );
}
